import {
  Box,
  Button,
  Checkbox,
  Dialog,
  Heading,
  HStack,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { BondInterestRateInputs } from '~/builders/bond-interest-rates/BondInterestRateInputs';
import { NumberInput, RealTimeCurrencyInput } from '~/components/inputs';
import type { ProjectData } from '~/hooks/useProjectStore';
import type { BondInterestRateSettings } from '~/types/app-settings';
import type { BondConfiguration } from '~/types/bond-inputs';

export interface BondConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (config: BondConfiguration) => void;
  project: ProjectData;
  /** Initial configuration values (optional - for pre-filling from quick add or editing) */
  initialConfig?: Partial<BondConfiguration>;
  /** Whether we're editing an existing bond (changes title and button text) */
  isEditing?: boolean;
}

export const BondConfigurationModal: React.FC<BondConfigurationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  project,
  initialConfig,
  isEditing = false,
}) => {
  // Get project's bond interest rate settings (fallback to defaults if needed)
  const projectInterestRates: BondInterestRateSettings = project.bondInterestRateSettings || {
    startRate: 3.0,
    incrementRate: 0.05,
    maxRate: 5.0,
  };

  // Form state
  const [issueDate, setIssueDate] = useState('');
  const [taxYear, setTaxYear] = useState('');
  const [durationYears, setDurationYears] = useState('');
  const [targetPrincipal, setTargetPrincipal] = useState('');

  // Interest rate override toggle
  const [overrideInterestRates, setOverrideInterestRates] = useState(false);
  const [customInterestRates, setCustomInterestRates] =
    useState<BondInterestRateSettings>(projectInterestRates);

  // Track if we've initialized the form for this modal open
  const hasInitializedRef = useRef(false);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIssueDate('');
      setTaxYear('');
      setDurationYears('');
      setTargetPrincipal('');
      setOverrideInterestRates(false);
      setCustomInterestRates(projectInterestRates);
      hasInitializedRef.current = false; // Reset for next open
    }
  }, [isOpen, projectInterestRates]);

  // Initialize form from initialConfig ONLY ONCE when modal opens
  useEffect(() => {
    if (isOpen && initialConfig && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      // Pre-fill form from initialConfig when modal opens
      if (initialConfig.issueDate) setIssueDate(initialConfig.issueDate);
      if (initialConfig.taxYear) setTaxYear(initialConfig.taxYear.toString());
      if (initialConfig.durationYears) setDurationYears(initialConfig.durationYears.toString());
      if (initialConfig.targetPrincipal) {
        setTargetPrincipal(initialConfig.targetPrincipal.toString());
      }
      if (initialConfig.interestRateSettings) {
        setOverrideInterestRates(true);
        setCustomInterestRates(initialConfig.interestRateSettings);
      }
    }
  }, [isOpen, initialConfig]);

  // Validation
  const isValid = useMemo(() => {
    return (
      issueDate !== '' &&
      taxYear !== '' &&
      parseInt(taxYear, 10) >= 2000 &&
      parseInt(taxYear, 10) <= 2100 &&
      durationYears !== '' &&
      parseInt(durationYears, 10) >= 1 &&
      parseInt(durationYears, 10) <= 40
    );
  }, [issueDate, taxYear, durationYears]);

  const handleSubmit = () => {
    if (!isValid) return;

    // Use tax year as the bond name identifier
    const autoName = taxYear;

    // Parse target principal (RealTimeCurrencyInput stores raw numeric string)
    const parsedPrincipal = targetPrincipal ? parseFloat(targetPrincipal) : undefined;

    const config: BondConfiguration = {
      name: autoName,
      issueDate,
      taxYear: parseInt(taxYear, 10),
      durationYears: parseInt(durationYears, 10),
      targetPrincipal:
        parsedPrincipal && !Number.isNaN(parsedPrincipal) ? parsedPrincipal : undefined,
      interestRateSettings: overrideInterestRates ? customInterestRates : undefined,
    };

    onSubmit(config);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  // Dynamic text based on edit mode
  const modalTitle = isEditing ? 'Update Bond' : 'Add Bond';
  const submitButtonText = isEditing ? 'Update Bond' : 'Add Bond';

  return (
    <Dialog.Root open={isOpen} onOpenChange={(e) => !e.open && handleClose()} placement="center">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content maxWidth="700px">
          <Dialog.Header>
            <Heading size="md">{modalTitle}</Heading>
          </Dialog.Header>

          <Dialog.Body>
            <VStack align="stretch" gap={4}>
              {/* Issue Date */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={1}>
                  Issue Date *
                </Text>
                <Input
                  type="date"
                  value={issueDate}
                  onChange={(e) => setIssueDate(e.target.value)}
                />
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Date when bond is issued
                </Text>
              </Box>

              {/* Tax Year */}
              <Box>
                <NumberInput
                  label="Tax Year *"
                  value={taxYear}
                  onChange={setTaxYear}
                  min={2000}
                  max={2100}
                  step={1}
                  useGrouping={false}
                  helperText="Dictates starting interest date and first payment date (must align with project dates)"
                />
              </Box>

              {/* Duration */}
              <Box>
                <NumberInput
                  label="Duration (Years) *"
                  value={durationYears}
                  onChange={setDurationYears}
                  min={1}
                  max={40}
                  step={1}
                  useGrouping={false}
                  helperText="Bond duration in years"
                />
              </Box>

              {/* Target Principal */}
              <Box>
                <RealTimeCurrencyInput
                  label="Target Principal (Optional)"
                  value={targetPrincipal}
                  onChange={setTargetPrincipal}
                  helperText="Target principal amount (leave empty to maximize capacity)"
                />
              </Box>

              {/* Interest Rate Override */}
              <Box p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
                <VStack align="stretch" gap={3}>
                  <Checkbox.Root
                    checked={overrideInterestRates}
                    onCheckedChange={(details) =>
                      setOverrideInterestRates(details.checked === true)
                    }
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                    <Checkbox.Label>
                      <Text fontSize="sm" fontWeight="medium">
                        Override Interest Rates for This Bond
                      </Text>
                    </Checkbox.Label>
                  </Checkbox.Root>

                  {overrideInterestRates && (
                    <Box>
                      <BondInterestRateInputs
                        values={customInterestRates}
                        onChange={(field, value) => {
                          const numValue = parseFloat(value);
                          if (Number.isNaN(numValue)) {
                            setCustomInterestRates((prev) => ({
                              ...prev,
                              [field]: value as unknown as number,
                            }));
                            return;
                          }
                          setCustomInterestRates((prev) => ({
                            ...prev,
                            [field]: numValue,
                          }));
                        }}
                      />
                    </Box>
                  )}

                  {!overrideInterestRates && (
                    <Text fontSize="xs" color="gray.600">
                      Using project settings: {projectInterestRates.startRate}% →{' '}
                      {projectInterestRates.maxRate}% (increment:{' '}
                      {projectInterestRates.incrementRate}%)
                    </Text>
                  )}
                </VStack>
              </Box>
            </VStack>
          </Dialog.Body>

          <Dialog.Footer>
            <HStack justify="flex-end" gap={2}>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button colorPalette="blue" onClick={handleSubmit} disabled={!isValid}>
                {submitButtonText}
              </Button>
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};
